import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { postPaymentOptions, postKlaviyo, klaviyoIdentify } from '@/services/forms.service';
import { getCountry } from '@/utils/i18n';
import Button from '@/components/homepage/Button';
import Input, { InputType } from '@/components/Form/Input';
import Title from '@/components/Title/Title';
import beTwMerge from '@/utils/beTwMerge';
import { getAnchorId } from '@/utils/builderUtils';
import useStore from '@/store/useStore';
import RichText from '@/components/RichText/RichText';
import { FadeIn } from '@/components/Footer/components/FadeIn';

interface IFinancingRequestForm {
  submitLabel?: string;
  title: string;
  description: string;
  successText: string;
  cta: {
    variant: 'primary' | 'secondary';
    color: 'light' | 'dark';
    label: string;
  };
  anchorLinkID?: string;
}

export default function FinancingRequestForm({
  title,
  description,
  cta,
  successText,
  anchorLinkID,
}: IFinancingRequestForm) {
  const [submitLabel, setSubmitLabel] = useState('');
  const user = useStore(state => state.user);
  const isEurope = getCountry().code === 'GB' || user?.isEuropeanUnionMember;
  const [formSettled, setFormSettled] = useState(false);

  function centerSuccessText() {
    setTimeout(() => {
      const element = document.getElementById('success-text')!;
      element.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
    }, 100);
  }

  const inputs: InputType[] = [
    {
      name: 'first_name',
      id: 'first_name',
      label: 'First Name',
      fullWidth: false,
      type: 'text',
      registerObject: {
        required: true,
      },
    },
    {
      name: 'last_name',
      id: 'last_name',
      label: 'Last Name',
      fullWidth: false,
      type: 'text',
      registerObject: {
        required: true,
      },
    },
    {
      name: 'email',
      id: 'email',
      label: 'Email',
      fullWidth: false,
      type: 'email',
      registerObject: {
        required: true,
        validate: {
          isValid: v => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v) || 'Invalid Email Address.',
        },
      },
    },
    {
      name: 'phone',
      id: 'phone',
      label: 'Phone Number',
      fullWidth: false,
      type: 'tel',
      registerObject: {
        validate: {
          isValid: v =>
            !v ||
            /^\(\d{3}\)[\s](\d{3})[-](\d{4})$/.test(v) ||
            /^\d{3}[-](\d{3})[-](\d{4})$/.test(v) ||
            /^\d{10}$/.test(v) ||
            'Invalid phone number.',
        },
      },
    },
    {
      name: 'subscription',
      id: 'subscription',
      label: isEurope
        ? 'Send me email updates on new styles and special offers from Brilliant Earth. I may withdraw my consent at any time through my account settings or by writing to privacy@brilliantearth.com.'
        : 'Send me updates on new styles and special offers.',
      type: 'checkbox',
      fullWidth: true,
      className: 'tw-mt-[-12px]',
    },
  ];

  const {
    register,
    getValues,
    trigger,
    setFocus,
    reset,
    formState: { errors },
  } = useForm<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    subscription: boolean;
    privacyPolicyTermsConditions?: boolean;
  }>({ defaultValues: { subscription: false } });

  if (isEurope) {
    inputs.push({
      name: 'privacyPolicyTermsConditions',
      id: 'privacyPolicyTermsConditions',
      fullWidth: true,
      type: 'checkbox',
      className: 'tw-mt-[-28px]',
      label:
        "I agree to Brilliant Earth's <a href='https://www.brilliantearth.com/en-gb/security-and-privacy-policies/#privacy' target='_blank'>Privacy Policy</a> and <a href='https://www.brilliantearth.com/en-gb/terms-and-conditions/' target='_blank'>Terms and Conditions</a>.",
      registerObject: {
        required: 'Agreement to our Privacy Policy and Terms and Conditions is required.',
      },
    });
  }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const getFirstError = useCallback(
    () => Object.keys(errors).reduce<any>((field, a) => (errors[field] ? field : a), null),
    [errors]
  );
  async function submitForm() {
    const isFormValid = await trigger();
    const { subscription, email, ...formData } = getValues();
    if (isFormValid) {
      setIsSubmitting(true);
      setSubmitLabel('Sending');
      if (subscription) {
        postKlaviyo('Jewelry Financing Page', email);
      }
      klaviyoIdentify({
        email,
        firstName: (formData as any).first_name,
        lastName: (formData as any).last_name,
      });
      postPaymentOptions({ subscription: subscription ? 'T' : 'F', email, ...formData })
        .then(({ status, data }) => {
          if (!status.error) {
            setFormSettled(true);
            reset();
            (window as any).dataLayer.push({
              userEmail: data.user_email,
              userHashedEmail: data.user_hashed_email,
              userPhoneNumber: data.user_phone_number,
              userFirstName: data.user_first_name,
              userLastName: data.user_last_name,
              hashed_user_email_md5: data.hashed_user_email_md5,
            });
            (window as any).dataLayer.push({ event: 'financing_request' });
            (window as any).track_event('Form submit', 'Financing Request', '');
            setSubmitLabel('Sent Successfully');
            centerSuccessText();
          } else {
            throw new Error('Failed to send');
          }
        })
        .catch(() => {
          // eslint-disable-next-line no-alert
          alert('Sent failed. Please try again.');
        })
        .finally(() => {
          setSubmitLabel(cta?.label);
          setIsSubmitting(false);
        });
    } else {
      const firstError = getFirstError();
      if (getFirstError()) setFocus<any>(firstError);
    }
  }
  useEffect(() => {
    const firstError = getFirstError();
    if (firstError) {
      setFocus<any>(firstError);
    }
  }, [getFirstError, setFocus]);

  return (
    <section
      className={beTwMerge('tw-max-w-[610px] tw-mx-4 sm:tw-mx-auto tw-mb-14 lg:tw-mb-20')}
      {...getAnchorId(anchorLinkID)}
    >
      <FadeIn props={{ opacity: formSettled ? 1 : 0 }} visible={formSettled}>
        <p id="success-text" className="tw-text-center">
          {successText}
        </p>
      </FadeIn>
      {!formSettled && (
        <div className="tw-w-full">
          <Title component="h2" weight="normal" size="large" alignment="center" className="!tw-mb-2">
            {title}
          </Title>
          <RichText className="tw-font-normal tw-text-center tw-mb-[3.25rem]" content={description} />
          <form>
            <div className="tw-grid md:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-11 tw-mb-10 sm:tw-mb-14">
              {inputs.map(input => (
                <>
                  {/* @ts-ignore */}
                  <Input
                    {...input}
                    key={input.name}
                    register={register as any}
                    name={input.name}
                    id={input.id}
                    className={input.className}
                    error={errors[input.name] as any}
                  />
                </>
              ))}
            </div>

            <div className="tw-col-span-full tw-flex tw-justify-center">
              <Button
                className="tw-min-w-[200px]"
                label={submitLabel || cta?.label}
                color={cta?.color === 'light' ? 'white' : 'dark-green'}
                type={cta?.variant}
                onClick={submitForm}
                disabled={isSubmitting}
              />
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
